import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { TrendingUp, PieChart as PieChartIcon, BarChart3 } from 'lucide-react';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export const TreasuryCharts = ({ transactions, categoryStats, stats }) => {
  // Prepare data for balance evolution chart (last 30 days)
  const prepareBalanceEvolutionData = () => {
    if (transactions.length === 0) return null;

    // Sort transactions by date (oldest first)
    const sortedTransactions = [...transactions].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );

    // Take last 30 transactions or all if less
    const recentTransactions = sortedTransactions.slice(-30);

    const labels = recentTransactions.map(t => {
      const date = new Date(t.created_at);
      return `${date.getDate()}/${date.getMonth() + 1}`;
    });

    const balances = recentTransactions.map(t => t.balance_after || 0);

    return {
      labels,
      datasets: [
        {
          label: 'Solde ($)',
          data: balances,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
        },
      ],
    };
  };

  // Prepare data for category doughnut chart
  const prepareCategoryData = () => {
    if (Object.keys(categoryStats).length === 0) return null;

    const topCategories = Object.entries(categoryStats)
      .sort((a, b) => b[1].total - a[1].total)
      .slice(0, 6);

    const labels = topCategories.map(([cat]) => getCategoryLabel(cat));
    const data = topCategories.map(([, stats]) => stats.total);

    const colors = [
      'rgba(239, 68, 68, 0.8)',    // Red
      'rgba(249, 115, 22, 0.8)',   // Orange
      'rgba(234, 179, 8, 0.8)',    // Yellow
      'rgba(34, 197, 94, 0.8)',    // Green
      'rgba(59, 130, 246, 0.8)',   // Blue
      'rgba(168, 85, 247, 0.8)',   // Purple
    ];

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors,
          borderColor: colors.map(c => c.replace('0.8', '1')),
          borderWidth: 2,
        },
      ],
    };
  };

  // Prepare data for income vs expenses bar chart
  const prepareIncomeExpenseData = () => {
    if (transactions.length === 0) return null;

    // Group by month
    const monthlyData = {};
    
    transactions.forEach(trans => {
      const date = new Date(trans.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { income: 0, expense: 0 };
      }
      
      if (trans.amount > 0) {
        monthlyData[monthKey].income += trans.amount;
      } else {
        monthlyData[monthKey].expense += Math.abs(trans.amount);
      }
    });

    // Sort by month and take last 6 months
    const sortedMonths = Object.keys(monthlyData).sort().slice(-6);
    
    const labels = sortedMonths.map(key => {
      const [year, month] = key.split('-');
      const date = new Date(year, month - 1);
      return date.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
    });

    const incomeData = sortedMonths.map(key => monthlyData[key].income);
    const expenseData = sortedMonths.map(key => monthlyData[key].expense);

    return {
      labels,
      datasets: [
        {
          label: 'Revenus',
          data: incomeData,
          backgroundColor: 'rgba(34, 197, 94, 0.8)',
          borderColor: 'rgb(34, 197, 94)',
          borderWidth: 2,
        },
        {
          label: 'Dépenses',
          data: expenseData,
          backgroundColor: 'rgba(239, 68, 68, 0.8)',
          borderColor: 'rgb(239, 68, 68)',
          borderWidth: 2,
        },
      ],
    };
  };

  const getCategoryLabel = (category) => {
    const labels = {
      'revenue_client': 'Paiement Client',
      'revenue_service': 'Revenu Service',
      'revenue_other': 'Autre Revenu',
      'expense_salary': 'Salaire',
      'expense_rent': 'Loyer',
      'expense_electricity': 'Électricité',
      'expense_internet': 'Internet',
      'expense_fuel': 'Gasoil',
      'expense_supplies': 'Fournitures',
      'expense_other': 'Autre Dépense',
    };
    return labels[category] || category;
  };

  const balanceData = prepareBalanceEvolutionData();
  const categoryData = prepareCategoryData();
  const incomeExpenseData = prepareIncomeExpenseData();

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  const lineOptions = {
    ...commonOptions,
    scales: {
      y: {
        beginAtZero: false,
        ticks: {
          callback: (value) => `${value.toFixed(0)} $`,
        },
      },
    },
  };

  const barOptions = {
    ...commonOptions,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `${value.toFixed(0)} $`,
        },
      },
    },
  };

  const doughnutOptions = {
    ...commonOptions,
    plugins: {
      ...commonOptions.plugins,
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: ${value.toFixed(2)} $ (${percentage}%)`;
          },
        },
      },
    },
  };

  if (transactions.length === 0) {
    return (
      <Card className="bg-gradient-to-br from-blue-50 to-purple-50">
        <CardContent className="p-12">
          <div className="text-center text-gray-500">
            <BarChart3 className="h-16 w-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium">Aucune donnée pour les graphiques</p>
            <p className="text-sm mt-2">Ajoutez des transactions pour visualiser les statistiques</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Balance Evolution Chart */}
      {balanceData && (
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              Évolution du Solde
            </CardTitle>
            <p className="text-sm text-gray-600">
              Historique des 30 dernières transactions
            </p>
          </CardHeader>
          <CardContent>
            <div style={{ height: '300px' }}>
              <Line data={balanceData} options={lineOptions} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Income vs Expenses Bar Chart */}
      {incomeExpenseData && (
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-green-600" />
              Revenus vs Dépenses
            </CardTitle>
            <p className="text-sm text-gray-600">
              Comparaison mensuelle des 6 derniers mois
            </p>
          </CardHeader>
          <CardContent>
            <div style={{ height: '300px' }}>
              <Bar data={incomeExpenseData} options={barOptions} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Category Breakdown Doughnut Chart */}
      {categoryData && (
        <Card className="bg-gradient-to-br from-purple-50 to-pink-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChartIcon className="h-5 w-5 text-purple-600" />
              Répartition par Catégorie
            </CardTitle>
            <p className="text-sm text-gray-600">
              Top 6 catégories de dépenses
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center" style={{ height: '350px' }}>
              <Doughnut data={categoryData} options={doughnutOptions} />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      <Card className="bg-gradient-to-br from-gray-50 to-slate-50">
        <CardHeader>
          <CardTitle>Résumé Financier</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-white rounded-lg border-l-4 border-green-500">
              <p className="text-sm text-gray-600">Total Revenus</p>
              <p className="text-2xl font-bold text-green-600">
                +{stats.totalRevenue.toFixed(2)} $
              </p>
            </div>
            <div className="p-4 bg-white rounded-lg border-l-4 border-red-500">
              <p className="text-sm text-gray-600">Total Dépenses</p>
              <p className="text-2xl font-bold text-red-600">
                -{stats.totalExpense.toFixed(2)} $
              </p>
            </div>
            <div className="p-4 bg-white rounded-lg border-l-4 border-blue-500">
              <p className="text-sm text-gray-600">Bilan Net</p>
              <p className={`text-2xl font-bold ${stats.totalRevenue - stats.totalExpense >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(stats.totalRevenue - stats.totalExpense >= 0 ? '+' : '')}{(stats.totalRevenue - stats.totalExpense).toFixed(2)} $
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TreasuryCharts;
